import { type MappaCatalogoLivelliDTOLivelli } from '@framework/api-client/api/users/schemas/mappaCatalogoLivelliDTOLivelli.ts';
import { assignUniqueLayerCode } from '../../../layers/assignUniqueLayerCode.ts';
import { type IMapGroup } from '../../../layers/MapGroup.ts';
import { type IMapLayer } from '../../../layers/MapLayer.ts';

/**
 * Rappresentazione tipizzata del blob JSON `livelli` salvato nel catalogo.
 * Il backend lo memorizza come `{ [key: string]: any }`, questa interfaccia
 * ne definisce la struttura attesa con layer e gruppi tipizzati.
 */
export interface ILayerCatalogLevels {
  layers: IMapLayer[];
  groups: IMapGroup[];
}

/**
 * Converte il campo `livelli` del DTO {@link MappaCatalogoLivelliDTOLivelli}
 * (tipizzato dal backend come `{ [key: string]: any } | null`) nella struttura
 * tipizzata {@link ILayerCatalogLevels}.
 *
 * Se il campo è `null` o `undefined` restituisce array vuoti.
 * I campi `layers` e `groups` vengono validati con `Array.isArray` prima del cast.
 */
export function parseLayerCatalogLevels(
  livelli: MappaCatalogoLivelliDTOLivelli | undefined,
): ILayerCatalogLevels {
  if (!livelli) {
    return { layers: [], groups: [] };
  }

  const rawLayers = Array.isArray(livelli.layers)
    ? (livelli.layers as IMapLayer[])
    : [];

  // I cataloghi legacy possono non avere `code` (i blob non passano per le
  // migrazioni sezioni): lo generiamo dal nome e ne garantiamo l'unicità
  // all'interno del catalogo.
  const usedCodes = new Set<string>();
  const layers = rawLayers.map(layer =>
    assignUniqueLayerCode(layer, usedCodes),
  );

  return {
    layers,
    groups: Array.isArray(livelli.groups)
      ? (livelli.groups as IMapGroup[])
      : [],
  };
}
